import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBiometricAuth } from '@/hooks/useBiometricAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Fingerprint, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

// Déploiement dédié Inside VNX (vnx.valnexo.fr) → après connexion, rester sur Inside VNX (pas le CRM)
const getPostLoginPath = () =>
    (typeof window !== 'undefined' && /^(vnx|inside)\./i.test(window.location.hostname)) ? '/inside-vnx' : '/dashboard';

// Logo Component - Image officielle Valnexo
const ValnexoLogo = ({ size = 50 }) => (
    <img 
        src="/valnexo-logo-official-new.png"
        alt="Valnexo Logo"
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: 6 }}
    />
);

export default function Login() {
    const navigate = useNavigate();
    const { login, setAuthData } = useAuth();
    const { 
        isAvailable: biometricAvailable, 
        isEnabled: biometricEnabled,
        isLoading: biometricLoading,
        isMobile,
        authenticateWithBiometric,
        saveSessionForBiometric
    } = useBiometricAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);

    // Check for biometric login on mount (mobile only)
    useEffect(() => {
        if (biometricEnabled && isMobile) {
            setShowBiometricPrompt(true);
        }
    }, [biometricEnabled, isMobile]);

    // Handle biometric authentication
    const handleBiometricLogin = async () => {
        const result = await authenticateWithBiometric();
        if (result.success && result.token) {
            // Restore session from biometric
            try {
                setAuthData(result.token);
                toast.success('Connexion biométrique réussie');
                navigate(getPostLoginPath());
            } catch (e) {
                toast.error('Session expirée, veuillez vous reconnecter');
                setShowBiometricPrompt(false);
            }
        } else {
            toast.error(result.error || 'Authentification échouée');
            setShowBiometricPrompt(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const response = await login(email.trim().toLowerCase(), password);
            
            // Save session for biometric reauth if enabled
            if (biometricEnabled && response?.access_token) {
                saveSessionForBiometric(response.access_token);
            }
            
            toast.success('Connexion réussie');
            navigate(getPostLoginPath());
        } catch (error) {
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                // Timeout (serveur lent / démarrage à froid) — message clair, pas "connexion internet"
                toast.error('Le serveur met trop de temps à répondre. Patientez quelques secondes et réessayez.');
            } else if (!error.response) {
                // Erreur réseau (pas de réponse du serveur)
                toast.error('Problème de connexion au serveur. Vérifiez votre connexion internet et réessayez.');
            } else if (error.response?.status === 401) {
                toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
            } else if (error.response?.status >= 500) {
                toast.error('Erreur temporaire du serveur. Réessayez dans quelques instants.');
            } else {
                toast.error(error.response?.data?.detail || 'Erreur de connexion');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Branding with Valnexo theme */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0B1F3B' }}>
                {/* Background image with overlay */}
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBoYXBweSUyMGNvbGxhYm9yYXRpb24lMjBzdWNjZXNzJTIwbmV0d29ya2luZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzA5MzMxNDl8MA&ixlib=rb-4.1.0&q=85')`,
                        opacity: 0.15
                    }}
                />
                
                {/* Gradient overlay */}
                <div 
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, rgba(11,31,59,0.95) 0%, rgba(11,31,59,0.8) 50%, rgba(30,91,255,0.3) 100%)'
                    }}
                />
                
                {/* Decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div 
                        className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl"
                        style={{ backgroundColor: '#1E5BFF' }}
                    />
                    <div 
                        className="absolute bottom-20 left-20 w-80 h-80 rounded-full opacity-10 blur-3xl"
                        style={{ backgroundColor: '#F2B705' }}
                    />
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    {/* Logo */}
                    <div className="flex items-center gap-4">
                        <ValnexoLogo size={55} />
                        <div>
                            <h1 className="font-bold text-2xl tracking-wide" style={{ color: '#F5F7FA' }}>VALNEXO</h1>
                            <p className="text-sm tracking-widest font-medium" style={{ color: '#1E5BFF' }}>CRM ÉNERGIE</p>
                        </div>
                    </div>
                    
                    {/* Main message */}
                    <div className="space-y-8">
                        <h2 className="text-4xl font-bold leading-tight" style={{ color: '#F5F7FA' }}>
                            L'union des talents,
                            <br />
                            <span style={{ color: '#F2B705' }}>la puissance du réseau.</span>
                        </h2>
                        <p className="text-lg opacity-90 max-w-md leading-relaxed" style={{ color: '#F5F7FA' }}>
                            Un réseau d'apporteurs d'affaires uni par la confiance.
                            Ensemble, créons des opportunités et partageons le succès.
                        </p>
                        
                        {/* Value props */}
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F2B705' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Réseau d'apporteurs d'affaires</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#1E5BFF' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Commissions transparentes</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22C55E' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Croissance partagée</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="text-sm opacity-60" style={{ color: '#F5F7FA' }}>
                        © 2026 Valnexo. Tous droits réservés.
                    </div>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md space-y-8">
                    {/* Mobile Logo */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <ValnexoLogo size={45} textColor="#0B1F3B" />
                        <div>
                            <h1 className="font-bold text-xl" style={{ color: '#0B1F3B' }}>VALNEXO</h1>
                            <p className="text-xs tracking-widest" style={{ color: '#1E5BFF' }}>CRM ÉNERGIE</p>
                        </div>
                    </div>

                    {/* Welcome */}
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#0B1F3B' }}>
                            Connexion
                        </h2>
                        <p className="mt-2 text-slate-600">
                            Accédez à votre espace de gestion
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 pr-12"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 text-white font-semibold transition-all hover:opacity-90"
                            style={{ backgroundColor: '#0B1F3B' }}
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                'Se connecter'
                            )}
                        </Button>
                        
                        {/* Mot de passe oublié */}
                        <div className="text-center">
                            <a 
                                href="/forgot-password" 
                                className="text-sm text-blue-600 hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Mot de passe oublié ?
                            </a>
                        </div>
                        
                        {/* Biometric Login Button - Mobile Only */}
                        {biometricEnabled && isMobile && showBiometricPrompt && (
                            <div className="pt-4 border-t border-slate-200">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-12 gap-3 border-slate-300 hover:bg-slate-50"
                                    onClick={handleBiometricLogin}
                                    disabled={biometricLoading}
                                    data-testid="biometric-login-btn"
                                >
                                    {biometricLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Fingerprint className="w-5 h-5 text-blue-600" />
                                            <span>Connexion Face ID / Touch ID</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </form>

                    {/* Footer info */}
                    <p className="text-center text-sm text-slate-500">
                        Problème de connexion ? Contactez votre administrateur.
                    </p>
                    
                    {/* PWA Install hint - Mobile Only */}
                    {isMobile && !window.matchMedia('(display-mode: standalone)').matches && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-start gap-3">
                                <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <p className="font-medium text-blue-900">Installez l'application</p>
                                    <p className="text-blue-700 mt-1">
                                        Ajoutez Valnexo à votre écran d'accueil pour une meilleure expérience.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
